import { TreatmentsWithConfig, TreatmentWithConfig } from '@splitsoftware/splitio/types/splitio';
import { isServerSide } from 'src/utils/validators/isServerSide';
import * as ApplicationConfigurationService from '../config/ApplicationConfigurationService.client';
import { DatadogRUMService } from '../datadog/DatadogRUMService';
import { getRawLocalStorageItem, getParsedLocalStorageItem } from '../storage/local';
import { ExperimentServiceDomain } from './constants';

declare global {
    // Required to extend global
    // eslint-disable-next-line no-var
    var zhlExperiments: TreatmentsWithConfig | undefined;
    // eslint-disable-next-line no-var
    var Cypress: boolean | undefined;
    // eslint-disable-next-line no-var
    var CypressEnableDownloads: boolean | undefined;
}

export { ExperimentServiceDomain as Domain };

export class ExperimentManager {
    static #instance: ExperimentManager;

    readonly #client = DatadogRUMService.Client.getInstance();

    private constructor() {}

    static getInstance(): ExperimentManager {
        if (!ExperimentManager.#instance) {
            ExperimentManager.#instance = new ExperimentManager();
        }
        return ExperimentManager.#instance;
    }

    public getExperiment(
        name: ExperimentServiceDomain.ActiveExperiment,
        serverSideExperiments?: TreatmentsWithConfig
    ): TreatmentWithConfig | null {
        const configService = ApplicationConfigurationService.ConfigProvider.getInstance();
        const clientSideLiveValue = globalThis.zhlExperiments?.[name] ?? null;
        const serverSideLiveValue = serverSideExperiments?.[name] ?? null;
        const liveValue = serverSideLiveValue ?? clientSideLiveValue;

        if (!configService.runtimeConfig.isLowerEnvironment) {
            return liveValue;
        }

        const localStorageValue = getRawLocalStorageItem(`${name}_override`);
        const localStorageOverridenValue: TreatmentWithConfig | null = localStorageValue
            ? { treatment: localStorageValue, config: null }
            : null;
        const emptyTreatments = {} as TreatmentsWithConfig;
        const cypressDefinedLocalExperiments = globalThis.Cypress
            ? getParsedLocalStorageItem<TreatmentsWithConfig>('localExperiments') ?? emptyTreatments
            : emptyTreatments;

        const cypressOverridenValue: TreatmentWithConfig | null = cypressDefinedLocalExperiments[name] ?? null;

        return cypressOverridenValue ?? localStorageOverridenValue ?? liveValue;
    }

    public initializeExperimentDatadogTracking = async (experiments: TreatmentsWithConfig) => {
        if (isServerSide()) {
            // eslint-disable-next-line no-console
            console.warn(
                'initializeExperimentDatadogTracking called while window was undefined. Did you accidentally call this server side?'
            );
            return;
        }

        globalThis.zhlExperiments = {
            ...globalThis.zhlExperiments,
            ...experiments,
        };
        Object.entries(globalThis.zhlExperiments).forEach(async ([experimentName, info]) => {
            await this.#client.addFeatureFlagEvaluation(experimentName, info.treatment);
        });
    };
}
